import { useCallback, useEffect, useState } from "react";
import { ShieldCheck, ShieldX, RefreshCw } from "lucide-react";
import { api, fmtTime } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { AccessReportButton } from "@/components/meridian/AccessReportButton";

function Matrix({ personas, permissions }) {
  return (
    <div className="panel overflow-x-auto" data-testid="permission-matrix">
      <div className="px-4 py-3 border-b border-slate-200 eyebrow">Access policy - who may do what</div>
      <table className="w-full text-xs">
        <thead><tr className="text-left text-slate-500"><th className="px-4 py-2 font-medium">Permission</th>{personas.map((p) => <th key={p.id} className="px-3 py-2 font-medium text-center">{p.name}<div className="text-[10px] font-normal">{p.role}</div></th>)}</tr></thead>
        <tbody>
          {Object.entries(permissions).map(([k, label]) => (
            <tr key={k} className="border-t border-slate-200">
              <td className="px-4 py-2 text-slate-700">{label}<span className="ml-2 num text-[10px] text-slate-400">{k}</span></td>
              {personas.map((p) => <td key={p.id} className="px-3 py-2 text-center">{p.permissions.includes(k) ? <ShieldCheck className="w-4 h-4 text-emerald-600 inline" /> : <ShieldX className="w-4 h-4 text-slate-300 inline" />}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AccessAudit() {
  const { personas } = useApp();
  const [perms, setPerms] = useState({});
  const [rows, setRows] = useState([]);
  const [denied, setDenied] = useState(true);
  const load = useCallback(() => api.get(`/audit/access?denied_only=${denied}`).then((r) => setRows(r.data)), [denied]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/personas").then((r) => setPerms(r.data.permissions)); }, []);
  return (
    <div className="flex-1 min-w-0 overflow-y-auto thin-scroll grid-bg" data-testid="access-audit-page">
      <div className="max-w-6xl px-8 py-8 space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
          <div className="eyebrow mb-2">Control Vector · Access audit</div>
          <h1 className="font-display text-4xl font-extrabold tracking-tight text-slate-900">Access audit log</h1>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">Every protected action is checked against the current persona's role, and every attempt is recorded, whether allowed or blocked. Use the persona switcher in the header to try actions as a different role.</p>
          </div>
          <AccessReportButton />
        </div>
        <Matrix personas={personas} permissions={perms} />
        <div className="panel">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
            <span className="eyebrow">Access decisions ({rows.length})</span>
            <div className="flex gap-2">
              {[[true, "Blocked only"], [false, "All attempts"]].map(([v, l]) => <button key={l} data-testid={`audit-filter-${v ? "denied" : "all"}`} onClick={() => setDenied(v)} className={`text-[11px] px-2 py-1 rounded border ${denied === v ? "border-sky-500/60 bg-sky-600/10 text-sky-800" : "border-slate-300 text-slate-600"}`}>{l}</button>)}
              <button data-testid="audit-refresh" onClick={load} className="text-[11px] px-2 py-1 rounded border border-slate-300 text-slate-600"><RefreshCw className="w-3 h-3" /></button>
            </div>
          </div>
          <table className="w-full text-xs" data-testid="access-log-table">
            <thead><tr className="text-left text-slate-500">{["Time", "Persona", "Action", "Resource", "Result", "Reason"].map((h) => <th key={h} className="px-4 py-2 font-medium">{h}</th>)}</tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className={`border-t border-slate-200 ${r.result === "Denied" ? "bg-red-500/5" : ""}`} data-testid={`access-row-${r.id}`}>
                  <td className="px-4 py-2 num text-slate-500 whitespace-nowrap">{fmtTime(r.at)}</td>
                  <td className="px-4 py-2"><div className="text-slate-900">{r.actor}</div><div className="text-[10px] text-slate-500">{r.role}</div></td>
                  <td className="px-4 py-2 text-slate-700">{r.action}</td>
                  <td className="px-4 py-2 num text-[10px] text-slate-500">{r.resource}</td>
                  <td className={`px-4 py-2 font-medium ${r.result === "Denied" ? "text-red-600" : "text-emerald-700"}`}>{r.result}</td>
                  <td className="px-4 py-2 text-slate-600">{r.reason}</td>
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-500">No entries yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
